import { initializeAnalytics, reset, type Environment } from "@fullscript/fs-analytics";

import { isProduction, isStaging } from "@shared/utils/fullscriptEnv/fullscriptEnv";

export type InitFsAnalyticsOptions = {
  usePatientFsaWriteKey?: boolean;
};

const FS_ANALYTICS_AUTHENTICATED_SESSION_KEY = "fs_analytics_authenticated_session";

const PATIENT_FS_ANALYTICS_PATH_PREFIXES = ["/u", "/r"] as const;

let initialized = false;

const shouldUsePatientFsaWriteKeyForPathname = (pathname: string): boolean =>
  PATIENT_FS_ANALYTICS_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const getFsAnalyticsEnvironment = (): Environment => {
  if (isProduction()) return "production";
  if (isStaging()) return "staging";

  return "dev";
};

const markFsAnalyticsAuthenticatedSession = (): void => {
  try {
    sessionStorage.setItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY, "1");
  } catch {
    // sessionStorage may be unavailable
  }
};

const resetFsAnalyticsAfterLogout = (): void => {
  try {
    if (!sessionStorage.getItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY)) {
      return;
    }
    sessionStorage.removeItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY);
    reset();
  } catch {
    // Do not block shell load if reset() or sessionStorage throws
  }
};

/**
 * Initialize fs-analytics. This is idempotent - calling it multiple times is safe.
 * If a key is provided, it will be stored for future calls.
 * Subsequent calls can omit the key if it was previously stored.
 */
const initFsAnalyticsCore = (options?: InitFsAnalyticsOptions): boolean => {
  const isProd = isProduction();
  let rudderstackJsFsaWriteKey: string;

  /**
   * Rudderstack write keys are publicly visible and have been moved here from their previous hardcoded locations in the define.mjs files.
   * Once Remix environment variables are implemented accross the app, we can remove this and use a window.ENV var instead.
   */
  if (options?.usePatientFsaWriteKey) {
    rudderstackJsFsaWriteKey = isProd
      ? "3Dm0qGvgK0u2vldsUHB07krjtbs"
      : "3DlxIJmi7pHLsiLZEksKFJzTXaC";
  } else if (isProd) {
    rudderstackJsFsaWriteKey = "3AUE7onO0U4QeKLba1iqgN6Eh28";
  } else {
    rudderstackJsFsaWriteKey = "3AUDusr65kwFmJrsVqahIb8Ojdc";
  }

  if (initialized) return true;

  initializeAnalytics(rudderstackJsFsaWriteKey, getFsAnalyticsEnvironment());
  initialized = true;

  return true;
};

const initFsAnalytics = (options?: InitFsAnalyticsOptions): boolean => {
  const result = initFsAnalyticsCore(options);
  markFsAnalyticsAuthenticatedSession();

  return result;
};

const initFsAnalyticsUnauth = (): void => {
  initFsAnalyticsCore();
  resetFsAnalyticsAfterLogout();
};

const resetFsAnalyticsRemixIfGuest = (initialProps: { userId?: string } | undefined): void => {
  if (initialProps?.userId) {
    markFsAnalyticsAuthenticatedSession();

    return;
  }
  resetFsAnalyticsAfterLogout();
};

export {
  initFsAnalytics,
  initFsAnalyticsCore,
  initFsAnalyticsUnauth,
  resetFsAnalyticsRemixIfGuest,
  shouldUsePatientFsaWriteKeyForPathname,
};
